// Molecule Studio AI - Zustand Store
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  Molecule, 
  RenderMode, 
  ExportFormat,
  MoleculeState, 
  UIState, 
  VisualizationState,
  ExportState,
  SettingsState,
  ComparisonState,
  SavedMolecule,
  VisualizationSettings,
  CameraState,
  ExportOptions,
  ToastMessage,
  Measurement,
  Annotation,
  ExportHistoryItem
} from '../types';

// ============================================================================
// Default Values
// ============================================================================

const defaultVisualizationSettings: VisualizationSettings = {
  renderMode: RenderMode.BALL_AND_STICK,
  atomScale: 0.4,
  bondThickness: 0.15,
  showHydrogens: true,
  showAtomLabels: false,
  showBonds: true,
  backgroundColor: '#0f172a',
  ambientLightIntensity: 0.4,
  directionalLightIntensity: 0.8,
  enableShadows: false,
  antialiasing: true,
  enableSSAO: false,
  colorScheme: 'cpk',
  atomColors: {},
};

const defaultCameraState: CameraState = {
  position: [10, 10, 10],
  target: [0, 0, 0],
  zoom: 1,
  fov: 50,
};

const defaultExportOptions: ExportOptions = {
  format: 'png',
  includeHydrogens: true,
  highResolution: true,
  transparentBackground: false,
  width: 1920,
  height: 1080,
  quality: 92,
};

// ============================================================================
// UI Store
// ============================================================================

export const useUIStore = create<UIState>()(
  subscribeWithSelector((set) => ({
    sidebarOpen: true,
    sidebarTab: 'input',
    mobileSidebarOpen: false,
    activeModal: null,
    loading: false,
    loadingMessage: '',
    error: null,
    toast: null,
    theme: 'dark',
    language: 'en',

    setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
    setSidebarTab: (tab: string) => set({ sidebarTab: tab as UIState['sidebarTab'] }),
    setMobileSidebarOpen: (open: boolean) => set({ mobileSidebarOpen: open }),
    setActiveModal: (modal: string | null) => set({ activeModal: modal }),
    setLoading: (loading: boolean, message = '') => set({ loading, loadingMessage: message }),
    setError: (error: string | null) => set({ error }),
    clearError: () => set({ error: null }),
    showToast: (toast: ToastMessage | null) => set({ toast }),
    hideToast: () => set({ toast: null }),
    setTheme: (theme: 'dark' | 'light') => set({ theme }),
    setLanguage: (language: string) => set({ language }),
  }))
);

// ============================================================================
// Molecule Store
// ============================================================================

export const useMoleculeStore = create<MoleculeState>()(
  subscribeWithSelector((set, get) => ({
    currentMolecule: null,
    moleculeHistory: [],
    historyIndex: -1,
    moleculeLibrary: [],
    isLoading: false,
    error: null,

    setCurrentMolecule: (molecule: Molecule | null) => {
      const state = get();
      const { currentMolecule, moleculeHistory, historyIndex } = state;
      
      // Add current to history if different
      if (currentMolecule && molecule && currentMolecule.id !== molecule.id) {
        const newHistory = moleculeHistory.slice(0, historyIndex + 1);
        newHistory.push(currentMolecule);
        // Keep only last 50 molecules
        if (newHistory.length > 50) newHistory.shift();
        
        set({
          currentMolecule: molecule,
          moleculeHistory: newHistory,
          historyIndex: newHistory.length - 1,
        });
      } else {
        set({ currentMolecule: molecule });
      }
    },

    clearMolecule: () => set({
      currentMolecule: null,
      historyIndex: -1,
    }),

    undo: () => {
      const { moleculeHistory, historyIndex } = get();
      if (historyIndex > 0) {
        set({ historyIndex: historyIndex - 1 });
      }
    },

    redo: () => {
      const { moleculeHistory, historyIndex } = get();
      if (historyIndex < moleculeHistory.length - 1) {
        set({ historyIndex: historyIndex + 1 });
      }
    },

    addToLibrary: (molecule: SavedMolecule) => {
      const { moleculeLibrary } = get();
      set({ moleculeLibrary: [...moleculeLibrary, molecule] });
    },

    removeFromLibrary: (id: string) => {
      const { moleculeLibrary } = get();
      set({ moleculeLibrary: moleculeLibrary.filter(m => m.id !== id) });
    },

    setLoading: (isLoading: boolean) => set({ isLoading }),
    setError: (error: string | null) => set({ error }),
  }))
);

// ============================================================================
// Visualization Store
// ============================================================================

export const useVisualizationStore = create<VisualizationState>()(
  subscribeWithSelector((set) => ({
    settings: defaultVisualizationSettings,
    cameraState: defaultCameraState,
    selectedAtom: null,
    selectedBond: null,
    highlightedAtoms: [],
    measurements: [],
    annotations: [],
    isAnimating: false,
    animationFrame: 0,

    setSettings: (settings: Partial<VisualizationSettings>) => 
      set((state) => ({
        settings: { ...state.settings, ...settings }
      })),

    setRenderMode: (renderMode: RenderMode) =>
      set((state) => ({
        settings: { ...state.settings, renderMode }
      })),

    setAtomScale: (atomScale: number) =>
      set((state) => ({
        settings: { ...state.settings, atomScale }
      })),

    setBondThickness: (bondThickness: number) =>
      set((state) => ({
        settings: { ...state.settings, bondThickness }
      })),

    toggleHydrogens: () =>
      set((state) => ({
        settings: { ...state.settings, showHydrogens: !state.settings.showHydrogens }
      })),

    toggleAtomLabels: () =>
      set((state) => ({
        settings: { ...state.settings, showAtomLabels: !state.settings.showAtomLabels }
      })),

    setCameraState: (cameraState: CameraState) => set({ cameraState }),
    resetCamera: () => set({ cameraState: defaultCameraState }),

    selectAtom: (atomId: string | null) => set({ 
      selectedAtom: atomId,
      selectedBond: null,
    }),

    selectBond: (bondId: string | null) => set({ 
      selectedBond: bondId,
      selectedAtom: null,
    }),

    clearSelection: () => set({
      selectedAtom: null,
      selectedBond: null,
    }),

    highlightAtoms: (atomIds: string[]) => set({ highlightedAtoms: atomIds }),

    addMeasurement: (measurement: Measurement) =>
      set((state) => ({
        measurements: [...state.measurements, measurement]
      })),

    removeMeasurement: (id: string) =>
      set((state) => ({
        measurements: state.measurements.filter(m => m.id !== id)
      })),

    clearMeasurements: () => set({ measurements: [] }),

    addAnnotation: (annotation: Annotation) =>
      set((state) => ({
        annotations: [...state.annotations, annotation]
      })),

    removeAnnotation: (id: string) =>
      set((state) => ({
        annotations: state.annotations.filter(a => a.id !== id)
      })),

    setAnimating: (isAnimating: boolean) => set({ isAnimating }),
    setAnimationFrame: (frame: number) => set({ animationFrame: frame }),
  }))
);

// ============================================================================
// Export Store
// ============================================================================

export const useExportStore = create<ExportState>()(
  subscribeWithSelector((set) => ({
    options: defaultExportOptions,
    recentFormats: ['png', 'pdb'] as ExportFormat[],
    exportHistory: [],

    setExportOptions: (options: Partial<ExportOptions>) =>
      set((state) => ({
        options: { ...state.options, ...options }
      })),

    setFormat: (format: ExportFormat) =>
      set((state) => ({
        options: { ...state.options, format }
      })),

    addRecentFormat: (format: ExportFormat) =>
      set((state) => {
        const formats = [format, ...state.recentFormats.filter(f => f !== format)].slice(0, 5);
        return { recentFormats: formats };
      }),

    addToHistory: (item: ExportHistoryItem) =>
      set((state) => ({
        exportHistory: [item, ...state.exportHistory].slice(0, 20)
      })),
  }))
);

// ============================================================================
// Settings Store
// ============================================================================

export const useSettingsStore = create<SettingsState>()(
  subscribeWithSelector((set) => ({
    general: {
      autoSave: true,
      saveInterval: 30000,
      showTooltips: true,
      showHints: true,
      compactMode: false,
      highContrastMode: false,
      reducedMotion: false,
    },
    visualization: {},
    api: {
      geminiApiKey: 'AIzaSyD8PZ3cfrBLiXRW2gxkyFgbJ9EPhe7Lggs',
      pubChemApiEnabled: true,
      defaultApi: 'gemini',
      cacheEnabled: true,
      cacheSize: 100,
    },
    performance: {
      maxAtoms: 500,
      highQualityRender: true,
      hardwareAcceleration: true,
      antialiasing: true,
      shadowQuality: 'medium',
    },

    updateGeneral: (settings: Partial<SettingsState['general']>) =>
      set((state) => ({
        general: { ...state.general, ...settings }
      })),

    updateVisualization: (settings: Partial<VisualizationSettings>) =>
      set((state) => ({
        visualization: { ...state.visualization, ...settings }
      })),

    updateAPI: (settings: Partial<SettingsState['api']>) =>
      set((state) => ({
        api: { ...state.api, ...settings }
      })),

    updatePerformance: (settings: Partial<SettingsState['performance']>) =>
      set((state) => ({
        performance: { ...state.performance, ...settings }
      })),
  }))
);

// ============================================================================
// Comparison Store
// ============================================================================

export const useComparisonStore = create<ComparisonState>()(
  subscribeWithSelector((set) => ({
    molecules: [],
    activeComparisonId: null,
    comparisonMode: false,
    similarityThreshold: 0.7,
    sortBy: 'name',
    sortOrder: 'asc',

    addMolecule: (molecule: Molecule) =>
      set((state) => ({
        molecules: [...state.molecules, molecule]
      })),

    removeMolecule: (id: string) =>
      set((state) => ({
        molecules: state.molecules.filter(m => m.id !== id)
      })),

    clearMolecules: () => set({ molecules: [] }),

    setActiveComparison: (id: string | null) => set({ activeComparisonId: id }),

    toggleComparisonMode: () =>
      set((state) => ({ comparisonMode: !state.comparisonMode })),

    setSimilarityThreshold: (threshold: number) =>
      set({ similarityThreshold: threshold }),

    setSortBy: (sortBy: 'name' | 'mw' | 'logp' | 'tpsa') => set({ sortBy }),
    setSortOrder: (sortOrder: 'asc' | 'desc') => set({ sortOrder }),
  }))
);

// ============================================================================
// Utility Store Hooks
// ============================================================================

// Selectors for commonly used state
export const useCurrentMolecule = () => useMoleculeStore((state) => state.currentMolecule);
export const useIsLoading = () => useMoleculeStore((state) => state.isLoading);
export const useError = () => useMoleculeStore((state) => state.error);
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useSidebarTab = () => useUIStore((state) => state.sidebarTab);
export const useVisualizationSettings = () => useVisualizationStore((state) => state.settings);
export const useRenderMode = () => useVisualizationStore((state) => state.settings.renderMode);

// Action hooks
export const useMoleculeActions = () => {
  const store = useMoleculeStore();
  return {
    setCurrentMolecule: store.setCurrentMolecule,
    clearMolecule: store.clearMolecule,
    undo: store.undo,
    redo: store.redo,
    addToLibrary: store.addToLibrary,
    removeFromLibrary: store.removeFromLibrary,
    setLoading: store.setLoading,
    setError: store.setError,
  };
};

export const useVisualizationActions = () => {
  const store = useVisualizationStore();
  return {
    setSettings: store.setSettings,
    setRenderMode: store.setRenderMode,
    setAtomScale: store.setAtomScale,
    setBondThickness: store.setBondThickness,
    toggleHydrogens: store.toggleHydrogens,
    toggleAtomLabels: store.toggleAtomLabels,
    setCameraState: store.setCameraState,
    resetCamera: store.resetCamera,
    selectAtom: store.selectAtom,
    selectBond: store.selectBond,
    clearSelection: store.clearSelection,
    highlightAtoms: store.highlightAtoms,
  };
};

export const useUIActions = () => {
  const store = useUIStore();
  return {
    setSidebarOpen: store.setSidebarOpen,
    setSidebarTab: store.setSidebarTab,
    setMobileSidebarOpen: store.setMobileSidebarOpen,
    setActiveModal: store.setActiveModal,
    setLoading: store.setLoading,
    setError: store.setError,
    clearError: store.clearError,
    showToast: store.showToast,
    hideToast: store.hideToast,
    setTheme: store.setTheme,
  };
};

